'use client';

import { GameGlyph } from './GameArt';
import type { Game } from '@/lib/games';

/**
 * The picture on a game's card.
 *
 * ── Why these are drawn and not photographed ────────────────────────────────
 *
 * The obvious answer to "give every game a cover" is forty-one screenshots.
 * They are the wrong picture: a shot of a match-three at 260px is a smear of
 * confetti, half of these games are mostly dark, and the shelf ends up looking
 * like a folder of thumbnails rather than a rack of games. It is also forty-one
 * binary files to keep in step with forty-one games that change.
 *
 * So a cover is generated: an arcade-cabinet backdrop in the game's own colour,
 * a decorative layer chosen by which shelf it sits on, and the game's mark —
 * the SAME drawing as the little chip beside its name (GameArt.tsx) — blown up
 * to be the subject of the picture. Adding a game still costs one entry in
 * lib/games.ts and nothing else.
 *
 * ── Why it still looks like forty-one different pictures ───────────────────
 *
 * The decor is seeded from the game's id, so the stars, rays and grids land
 * somewhere different on every card and stay there. A fixed layout under
 * forty-one different glyphs reads as a template; a varied one reads as art
 * direction. `mulberry32` rather than `Math.random` precisely so the picture is
 * the same on every render — a cover that reshuffles when React re-renders is
 * worse than a template.
 *
 * ── Why the palette is hard-coded here ──────────────────────────────────────
 *
 * These are SVG gradient stops, not classes, so Tailwind never sees them and
 * they are the same in light and dark mode on purpose: the covers are meant to
 * read as lit screens sitting in the page, the way `body.vivid` does inside the
 * games themselves. A neon cabinet does not have a light mode.
 */

type Ramp = { deep: string; mid: string; hot: string };

const RAMPS: Record<string, Ramp> = {
  indigo: { deep: '#140a3f', mid: '#3b2ba8', hot: '#93a5ff' },
  red: { deep: '#2e0910', mid: '#8f1d2e', hot: '#ff8494' },
  teal: { deep: '#042521', mid: '#0d6b63', hot: '#5eead4' },
  purple: { deep: '#1d0940', mid: '#5b21b6', hot: '#c9aaff' },
  yellow: { deep: '#2c2104', mid: '#a3800a', hot: '#ffd76e' },
  orange: { deep: '#2c1304', mid: '#b3500d', hot: '#ffb066' },
  green: { deep: '#04210e', mid: '#128a3c', hot: '#6ee7a0' },
  blue: { deep: '#04193a', mid: '#1553b8', hot: '#7db3ff' },
  rose: { deep: '#2e0718', mid: '#a81447', hot: '#ff92b2' },
  fuchsia: { deep: '#280730', mid: '#a115a8', hot: '#f0a6ff' },
  lime: { deep: '#152105', mid: '#6a9c0e', hot: '#cdf06a' },
  cyan: { deep: '#04212a', mid: '#0e7d96', hot: '#77e6f7' },
  pink: { deep: '#2c071c', mid: '#b01262', hot: '#ffa3cf' },
  amber: { deep: '#2c1b04', mid: '#b3760d', hot: '#ffd08a' },
  sky: { deep: '#041d2b', mid: '#0f6fa3', hot: '#8ed7ff' },
  emerald: { deep: '#042017', mid: '#0f7a4e', hot: '#6ee7b7' },
  violet: { deep: '#1a0c3d', mid: '#6d28d9', hot: '#c9b8ff' },
  /* The thirty-one games with no tint of their own get the app's blue, which
     is what their chips already use. */
  accent: { deep: '#04162f', mid: '#0b57d0', hot: '#a8c7fa' },
};

/** A tiny seeded generator, so a cover is drawn the same way every time. */
function seedOf(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const W = 320;
const H = 200;

export default function GameCover({
  game,
  className = '',
}: {
  game: Game;
  className?: string;
}) {
  const ramp = RAMPS[game.tint ?? 'accent'] ?? RAMPS.accent;
  const uid = `gc-${game.id}`;
  const rnd = mulberry32(seedOf(game.id));

  /* Rolled once, up front, so the JSX below is layout and not logic. */
  const tilt = -26 + rnd() * 52;
  const originX = 40 + rnd() * 240;
  const stars = Array.from({ length: 34 }, () => ({
    x: rnd() * W,
    y: rnd() * H,
    r: 0.5 + rnd() * 1.6,
    o: 0.16 + rnd() * 0.5,
  }));
  const sparks = Array.from({ length: 3 }, () => ({
    x: 24 + rnd() * (W - 48),
    y: 18 + rnd() * (H - 36),
    s: 5 + rnd() * 7,
  }));
  const gridOff = rnd() * 22;
  const litCells = Array.from({ length: 4 }, () => ({
    x: Math.floor(rnd() * 14) * 24,
    y: Math.floor(rnd() * 9) * 24,
  }));
  const arcX = rnd() < 0.5 ? -30 : W + 30;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={className}
      role="img"
      aria-label={game.name}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={ramp.mid} />
          <stop offset="55%" stopColor={ramp.deep} />
          <stop offset="100%" stopColor="#05030f" />
        </linearGradient>
        {/* The light the subject is standing in. */}
        <radialGradient id={`${uid}-glow`} cx="50%" cy="52%" r="58%">
          <stop offset="0%" stopColor={ramp.hot} stopOpacity="0.55" />
          <stop offset="60%" stopColor={ramp.hot} stopOpacity="0.10" />
          <stop offset="100%" stopColor={ramp.hot} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-fade`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.45" />
        </linearGradient>
        <filter id={`${uid}-soft`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      <rect width={W} height={H} fill={`url(#${uid}-bg)`} />

      {/* ── The decorative layer, chosen by shelf ─────────────────────────
          Not decoration for its own sake: it is the fastest signal on the
          card of what KIND of thing this is, read before the name is. */}
      {game.group === 'big' && (
        <g transform={`rotate(${tilt} ${originX} ${H / 2})`} opacity="0.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <rect
              key={i}
              x={-200}
              y={-60 + i * 46}
              width={760}
              height={12 + i * 3}
              fill={ramp.hot}
              opacity={0.05 + i * 0.025}
            />
          ))}
        </g>
      )}

      {game.group === 'arcade' && (
        <g>
          {stars.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#fff" opacity={s.o} />
          ))}
          {sparks.map((s, i) => (
            <path
              key={`k${i}`}
              d={`M${s.x} ${s.y - s.s}L${s.x + s.s * 0.28} ${s.y}L${s.x} ${s.y + s.s}L${s.x - s.s * 0.28} ${s.y}Z
                  M${s.x - s.s} ${s.y}L${s.x} ${s.y - s.s * 0.28}L${s.x + s.s} ${s.y}L${s.x} ${s.y + s.s * 0.28}Z`}
              fill="#fff"
              opacity="0.5"
            />
          ))}
        </g>
      )}

      {game.group === 'puzzle' && (
        <g>
          {litCells.map((c, i) => (
            <rect key={`c${i}`} x={c.x} y={c.y} width="24" height="24" fill={ramp.hot} opacity="0.09" />
          ))}
          <g stroke="#fff" strokeOpacity="0.10" strokeWidth="1">
            {Array.from({ length: 14 }, (_, i) => (
              <line key={`v${i}`} x1={gridOff + i * 24} y1={0} x2={gridOff + i * 24} y2={H} />
            ))}
            {Array.from({ length: 9 }, (_, i) => (
              <line key={`h${i}`} x1={0} y1={gridOff + i * 24} x2={W} y2={gridOff + i * 24} />
            ))}
          </g>
        </g>
      )}

      {game.group === 'board' && (
        <g fill="none" stroke={ramp.hot} strokeOpacity="0.14">
          {[60, 110, 160, 210, 260].map((r, i) => (
            <circle key={i} cx={arcX} cy={H} r={r} strokeWidth={i === 1 ? 3 : 1.4} />
          ))}
        </g>
      )}

      <rect width={W} height={H} fill={`url(#${uid}-glow)`} />

      {/* ── The subject ───────────────────────────────────────────────────
          Drawn twice: once blurred in the hot colour, which is the glow, and
          once crisp in white on top. One pass looks flat against a lit
          background; the pair looks like a sign. */}
      <g filter={`url(#${uid}-soft)`} opacity="0.85">
        <svg x={W / 2 - 62} y={H / 2 - 62} width="124" height="124" viewBox="0 0 40 40"
             fill="none" stroke={ramp.hot} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
             style={{ color: ramp.hot }}>
          <GameGlyph art={game.art} />
        </svg>
      </g>
      <svg x={W / 2 - 62} y={H / 2 - 62} width="124" height="124" viewBox="0 0 40 40"
           fill="none" stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
           style={{ color: '#ffffff' }}>
        <GameGlyph art={game.art} />
      </svg>

      {/* Grounds the picture, and gives the name below it something to sit on. */}
      <rect y={H * 0.55} width={W} height={H * 0.45} fill={`url(#${uid}-fade)`} />
    </svg>
  );
}
